import type { UserState } from './useUser';
import { CompanyPermissionsCodename, PermissionsCodename } from '../../api';

const companyHas = (
  state: UserState,
  permission: CompanyPermissionsCodename
): boolean =>
  state.user?.company_permissions?.some(
    (p) => p.permission === permission && p.company_has_permission
  ) ?? false;

export const userPermissionGetters = {
  canInviteColleagues: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.INVITE_COLLEAGUES) ??
      false
    );
  },
  canUpdateColleague: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_COLLEAGUE) ??
      false
    );
  },
  canDeactivateColleague: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DEACTIVATE_COLLEAGUE
      ) ?? false
    );
  },
  canGetColleagueDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.GET_COLLEAGUE) ??
      false
    );
  },
  canUpdateColleagueWorkingHours: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_COLLEAGUE_WORKING_HOURS
      ) ?? false
    );
  },
  canGetColleagueWorkingHoursDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_COLLEAGUE_WORKING_HOURS
      ) ?? false
    );
  },
  canUpdateColleagueRole: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_COLLEAGUE_ROLE
      ) ?? false
    );
  },
  canEditCompanyDetails: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.EDIT_COMPANY_DETAILS
      ) ?? false
    );
  },
  canCreateOrder: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_ORDER) ??
      false
    );
  },
  canPayOrder: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.PAY_ORDER) ?? false
    );
  },
  canCreatePatient: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_PATIENT) ??
      false
    );
  },
  canUpdatePatient: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_PATIENT) ??
      false
    );
  },
  canRemovePatient: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.REMOVE_PATIENT) ??
      false
    );
  },
  canCreatePatientTicket: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_PATIENT_TICKET
      ) ?? false
    );
  },
  canMergePatients: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.MERGE_PATIENTS) ??
      false
    );
  },
  canCreateService: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_SERVICE) ??
      false
    );
  },
  canUpdateService: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_SERVICE) ??
      false
    );
  },
  canDeleteService: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.REMOVE_SERVICE) ??
      false
    );
  },
  canCreateTicket: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_TICKET) ??
      false
    );
  },
  canUpdateTicket: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_TICKET) ??
      false
    );
  },
  canGetTicketDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.GET_TICKET_DETAIL) ??
      false
    );
  },
  canGetServiceDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_SERVICE_DETAIL
      ) ?? false
    );
  },
  canCreateColleagueTimeOff: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_COLLEAGUE_TIME_OFF
      ) ?? false
    );
  },
  canUpdateColleagueTimeOff: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_COLLEAGUE_TIME_OFF
      ) ?? false
    );
  },
  canDeleteColleagueTimeOff: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DELETE_COLLEAGUE_TIME_OFF
      ) ?? false
    );
  },
  canGetSubscription: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.GET_SUBSCRIPTION) ??
      false
    );
  },
  canCreateSubscription: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_SUBSCRIPTION
      ) ?? false
    );
  },
  canDeleteSubscription: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.REMOVE_SUBSCRIPTION
      ) ?? false
    );
  },
  canUpdateSubscription: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_SUBSCRIPTION
      ) ?? false
    );
  },
  canCreateSubscriptionPayment: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_SUBSCRIPTION_PAYMENT
      ) ?? false
    );
  },
  canUpdateSubscriptionPayment: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_SUBSCRIPTION_PAYMENT
      ) ?? false
    );
  },
  canViewColleagueVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_COLLEAGUE_VISIT_DETAIL
      ) ?? false
    );
  },
  canCreateRoom: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_ROOM) ?? false
    );
  },
  canUpdateRoom: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_ROOM) ?? false
    );
  },
  canDeleteRoom: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.REMOVE_ROOM) ?? false
    );
  },
  canExportPatients: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.EXPORT_PATIENTS) ??
      false
    );
  },
  canExportPatientsAndVisits: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.EXPORT_PATIENTS_AND_VISITS
      ) ?? false
    );
  },
  canCreateReceipt: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_SELF_RECEIPT
      ) ?? false
    );
  },
  canUpdateReceipt: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_SELF_RECEIPT
      ) ?? false
    );
  },
  canCreateVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_VISIT) ??
      false
    );
  },
  canGetVisitDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.GET_VISIT_DETAIL) ??
      false
    );
  },
  canUpdateVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_VISIT) ??
      false
    );
  },
  canCreateGroupVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_GROUP_VISIT
      ) ?? false
    );
  },
  canUpdateGroupVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_GROUP_VISIT
      ) ?? false
    );
  },
  canGetGroupVisitDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_GROUP_VISIT_DETAIL
      ) ?? false
    );
  },
  canRemoveGroupVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.REMOVE_GROUP_VISIT
      ) ?? false
    );
  },
  canDeleteVisitReview: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DELETE_VISIT_REVIEW
      ) ?? false
    );
  },
  canViewCalendarUsers: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_CALENDAR_USERS_SUBTAB
      ) ?? false
    );
  },
  canViewCalendarRooms: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_CALENDAR_ROOMS_SUBTAB
      ) ?? false
    );
  },
  canViewCalendarPlanning: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_CALENDAR_PLANNING_SUBTAB
      ) ?? false
    );
  },
  canViewCalendarRoomsPlanning: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_CALENDAR_ROOMS_PLANNING_SUBTAB
      ) ?? false
    );
  },
  canViewMenuSubTabOfficeExercises: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_OFFICE_EXERCISES_SUBTAB
      ) ?? false
    );
  },
  canViewInteractiveGuide: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.GET_GUIDE_DATA) ??
      false
    );
  },
  canSendBatchToPurobot: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPLOAD_BATCHES_TO_PUROBOT
      ) ?? false
    );
  },
  canCreateLocation: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.CREATE_LOCATION) ??
      false
    );
  },
  canUpdateLocation: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPDATE_LOCATION) ??
      false
    );
  },
  canDeleteLocation: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.REMOVE_LOCATION) ??
      false
    );
  },
  canSetColleaguePassword: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.SET_COLLEAGUE_PASSWORD
      ) ?? false
    );
  },

  canCreateIssuedFTTicket: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_PATIENT_ISSUED_FT_TICKET
      ) ?? false
    );
  },

  // API přístupy (service accounts). Nejsou v základní výbavě žádné role - uděluje se
  // per firma přes CompanyPermissionGroup.extra_permissions v Django adminu.
  canListServiceAccounts: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.LIST_SERVICE_ACCOUNTS
      ) ?? false
    );
  },
  canGetServiceAccountDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_SERVICE_ACCOUNT_DETAIL
      ) ?? false
    );
  },
  canCreateServiceAccount: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_SERVICE_ACCOUNT
      ) ?? false
    );
  },
  canUpdateServiceAccount: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPDATE_SERVICE_ACCOUNT
      ) ?? false
    );
  },
  canDeleteServiceAccount: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DELETE_SERVICE_ACCOUNT
      ) ?? false
    );
  },
  canCreateServiceAccountToken: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.CREATE_SERVICE_ACCOUNT_TOKEN
      ) ?? false
    );
  },
  canRevokeServiceAccountToken: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.REVOKE_SERVICE_ACCOUNT_TOKEN
      ) ?? false
    );
  },
  // Visits — mobile visit action sheet.
  canRemoveVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.REMOVE_VISIT) ??
      false
    );
  },
  canDeleteColleagueVisit: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DELETE_COLLEAGUE_VISIT
      ) ?? false
    );
  },
  canGetPatientDetail: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.GET_PATIENT_DETAIL
      ) ?? false
    );
  },
  // Visit photos.
  canListVisitImages: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.LIST_VISIT_IMAGES) ??
      false
    );
  },
  canUploadVisitImage: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.UPLOAD_VISIT_IMAGE
      ) ?? false
    );
  },
  canDeleteVisitImage: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.DELETE_VISIT_IMAGE
      ) ?? false
    );
  },
  // Visit documents (forms).
  canListVisitForms: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.LIST_VISIT_FORMS) ??
      false
    );
  },
  canUploadVisitForm: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.UPLOAD_VISIT_FORM) ??
      false
    );
  },
  canDeleteVisitForm: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(PermissionsCodename.DELETE_VISIT_FORM) ??
      false
    );
  },
  // Waiting-clients entry point in the FAB sheet.
  canViewPatientsWaitingSubtab: (state: UserState): boolean => {
    return (
      state.user?.permissions.includes(
        PermissionsCodename.VIEW_PATIENTS_WAITING_SUBTAB
      ) ?? false
    );
  },
  // The waiting-clients feature ("Čekající") as a whole: the user's subtab
  // permission plus the company gates of its web subtab (PatientListNavBar.vue).
  // A package without them (START) must not offer waiting clients anywhere.
  canUseWaitingPatients: (state: UserState): boolean => {
    return (
      (state.user?.permissions.includes(
        PermissionsCodename.VIEW_PATIENTS_WAITING_SUBTAB
      ) ??
        false) &&
      companyHas(state, CompanyPermissionsCodename.CREATE_NEXT_PATIENT) &&
      companyHas(state, CompanyPermissionsCodename.ALLOW_CALENDAR)
    );
  },
  // Rooms and locations (Ordinace). START lacks it and their endpoints
  // answer 403, so forms skip loading and showing those fields.
  canUseOffice: (state: UserState): boolean =>
    companyHas(state, CompanyPermissionsCodename.ALLOW_OFFICE),
};
